import {
  ArrowBack as ArrowBackIcon,
  Assignment as InspectionIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  CircularProgress,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import { useLocale, useRedirect, useTranslate } from 'react-admin'

import type { EvidenceStepView } from '../../../domain/activeInspection'
import { buildEvidenceSteps, InspectionState } from '../../../domain/activeInspection'
import { NO_VALUE } from '../../../domain/activeInspectionCells'
import { nameEvidenceArchive } from '../../../domain/evidenceArchive'
import { acceptsEvidenceUploads } from '../../../domain/liveEvidenceCapture'
import { useFileDownload } from '../../../hooks'
import type { Locale } from '../../../i18n'
import { getRemoteUrl } from '../../../lib/supabase'
import type { ActiveInspection, UnclassifiedSeal } from '../../../models/activeInspection'
import { EvidenceStepStrip } from '../../atoms/EvidenceStepStrip'
import { ImageModal } from '../../atoms/ImageModal'
import { InspectionStateBadge } from '../../atoms/InspectionStateBadge'
import { ManualInspectionMarker } from '../../atoms/ManualInspectionMarker'
import { DeleteInspectionButton } from '../../molecules/DeleteInspectionButton'
import { StepEvidenceUploadButton } from '../../molecules/StepEvidenceUploadButton'
import { detailsHeaderSx } from '../detailsHeader'
import { Timeline } from '../HistoricalInspectionDetails/Timeline'

export interface InspectionDetailsProps {
  inspection: ActiveInspection
}

/** The bucket every inspection image lives in, on both lifecycles. */
const BUCKET = 'inspections'

const BackButton: FC = () => {
  const theme = useTheme()
  const redirect = useRedirect()
  const translate = useTranslate()
  const onClick = useCallback(() => {
    redirect('list', 'inspections')
  }, [redirect])
  const label = translate('common.back')

  return (
    <Tooltip title={label}>
      <IconButton
        onClick={onClick}
        size="medium"
        sx={{
          color: theme.palette.text.primary,
          mr: 1,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
        aria-label={label}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  )
}

/**
 * The zip's name follows the archived screen's download to the letter, so the
 * same inspection downloads under the same name before and after archival.
 */
const zipFilename = (inspection: ActiveInspection): string =>
  `inspection_${inspection.container.replace(/\s+/g, '_')}${inspection.day ? `_${inspection.day}` : ''}.zip`

const DownloadAllButton: FC<{ inspection: ActiveInspection }> = ({ inspection }) => {
  const { downloadFiles, downloadStatus } = useFileDownload()
  const translate = useTranslate()
  const isDownloading = downloadStatus === 'downloading'

  // Every file the inspection owns, named in the order the page renders it:
  // the timeline's steps, then the pending seals group. The step list is built
  // from the summary view for any state, because a cancelled inspection's strip
  // is empty but its timeline still shows the evidence it collected.
  const files = useMemo(
    () =>
      nameEvidenceArchive(inspection.container, buildEvidenceSteps(inspection.summaryView, inspection.state), [
        ...inspection.evidenceFiles.filter(key => !inspection.unclassifiedSeals.some(seal => seal.id === key)),
        ...inspection.unclassifiedSeals.map(seal => seal.id),
      ]).map(({ key, name }) => ({ name, url: getRemoteUrl(BUCKET, key) })),
    [inspection],
  )

  const handleDownload = useCallback(() => {
    void downloadFiles(files, zipFilename(inspection))
  }, [downloadFiles, files, inspection])

  const label = translate('pages.historical_inspections.download_all')

  return (
    <Button
      color="primary"
      variant="text"
      size="small"
      onClick={handleDownload}
      disabled={isDownloading || files.length === 0}
      startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
      aria-label={label}
    >
      {label}
    </Button>
  )
}

const SubheaderInfo: FC<{ inspection: ActiveInspection; locale: Locale }> = ({ inspection, locale }) => {
  const theme = useTheme()
  const translate = useTranslate()

  // Undated until SOSTRAT first matches a manual inspection.
  const formattedDate = inspection.day
    ? new Date(inspection.day).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : NO_VALUE

  return (
    <Typography variant="subtitle1" color="text.secondary" sx={{ ml: { xs: 0, sm: 6 } }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.light }} />
          {translate(`resources.historical_inspections.enums.location.${inspection.location}`)}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.light }} />
          {formattedDate}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InspectionIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.light }} />
          {translate(`resources.historical_inspections.enums.inspection_level.${inspection.inspectionLevel}`)}
        </Box>
      </Stack>
    </Typography>
  )
}

const InspectionHeader: FC<{ inspection: ActiveInspection; locale: Locale }> = ({ inspection, locale }) => {
  const theme = useTheme()

  return (
    <CardHeader
      title={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BackButton />
          <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {inspection.container}
              {inspection.isLocal && <ManualInspectionMarker synced={inspection.seenInSostrat} />}
            </Box>
            <InspectionStateBadge state={inspection.state} statusId={inspection.statusId} type={inspection.type} />
            {/*
              The strip the row carries on the Operativa table, built there with
              the inspection's derived state: empty for a cancelled inspection,
              blue on the step being worked only while it is IN_PROGRESS.
            */}
            <EvidenceStepStrip steps={inspection.evidenceSteps} size={32} />
          </Typography>
        </Box>
      }
      subheader={<SubheaderInfo inspection={inspection} locale={locale} />}
      action={
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <DownloadAllButton inspection={inspection} />
          <DeleteInspectionButton record={inspection} redirectOnSuccess />
        </Stack>
      }
      sx={detailsHeaderSx(theme)}
    />
  )
}

/**
 * Seals whose analysis has not finished. They belong to no step yet, so the
 * timeline cannot show them; they still exist, and an operator looking for a
 * seal that was just photographed finds it here rather than nowhere.
 */
const PendingSealsGallery: FC<{ seals: UnclassifiedSeal[]; onImageClick: (imagePath: string) => void }> = ({ seals, onImageClick }) => {
  const theme = useTheme()
  const translate = useTranslate()

  if (seals.length === 0) {
    return null
  }

  const title = translate('pages.inspections.pending_seals')

  return (
    <Box
      data-testid="pending-seals"
      sx={{
        bgcolor: theme.palette.background.paper,
        borderRadius: theme.spacing(1),
        p: theme.spacing(3),
        mb: theme.spacing(3),
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      <Grid2 container spacing={2}>
        {seals.map(seal => {
          const url = getRemoteUrl(BUCKET, seal.id)

          return (
            <Grid2 size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={seal.id}>
              <Card
                sx={{ position: 'relative', borderRadius: theme.spacing(1), overflow: 'hidden', cursor: 'pointer' }}
                onClick={() => {
                  onImageClick(url)
                }}
              >
                <CardMedia
                  component="img"
                  height={120}
                  image={url}
                  alt={`${title} - ${seal.serialId ?? seal.id}`}
                  sx={{ objectFit: 'cover' }}
                />
                {seal.serialId && (
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      color: 'white',
                      fontWeight: 600,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                    }}
                  >
                    {seal.serialId}
                  </Typography>
                )}
              </Card>
            </Grid2>
          )
        })}
      </Grid2>
    </Box>
  )
}

/**
 * The details of a live inspection of the Operativa table: the archived
 * screen's header and per-step galleries, fed by the row the table already
 * derives instead of an archived record. What differs is what the lifecycle
 * makes different — the state badge, the strip built with the row's own state,
 * the hard delete of a manual inspection, and a group for seals the analysis
 * has not placed yet.
 */
export const InspectionDetails: FC<InspectionDetailsProps> = ({ inspection }) => {
  const [modalImage, setModalImage] = useState<string | null>(null)
  const theme = useTheme()
  const locale = useLocale() as Locale

  // The galleries list the strip's steps, with the strip's statuses. A cancelled
  // inspection's strip is empty — its gaps are not gaps — but whatever evidence
  // it did collect still exists, so those steps keep their galleries.
  const timelineSteps = useMemo(
    () =>
      inspection.state === InspectionState.CANCELLED
        ? buildEvidenceSteps(inspection.summaryView, InspectionState.CANCELLED).filter(step => step.count > 0)
        : inspection.evidenceSteps,
    [inspection],
  )

  // Every step takes uploads while the app still lists the inspection, and none
  // once it does not: the app's own rule, keyed on the raw status.
  const renderStepAction = useMemo(() => {
    if (!acceptsEvidenceUploads(inspection.statusId)) return undefined
    const StepAction = (step: EvidenceStepView) => (
      <StepEvidenceUploadButton target={{ inspectionId: inspection.id, container: inspection.container, stepId: step.stepId }} />
    )
    return StepAction
  }, [inspection.container, inspection.id, inspection.statusId])

  const handleImageClick = useCallback((imagePath: string) => {
    setModalImage(imagePath)
  }, [])
  const handleCloseModal = useCallback(() => {
    setModalImage(null)
  }, [])

  return (
    <Box>
      <Card
        elevation={2}
        sx={{
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
          mb: theme.spacing(3),
        }}
      >
        <InspectionHeader inspection={inspection} locale={locale} />

        <CardContent sx={{ p: theme.spacing(3) }}>
          <Paper
            elevation={0}
            sx={{
              p: theme.spacing(2),
              bgcolor: theme.palette.background.default,
            }}
          >
            <Timeline steps={timelineSteps} onImageClick={handleImageClick} locale={locale} renderStepAction={renderStepAction} />
            <PendingSealsGallery seals={inspection.unclassifiedSeals} onImageClick={handleImageClick} />
          </Paper>
        </CardContent>

        <ImageModal open={!!modalImage} imagePath={modalImage} onClose={handleCloseModal} />
      </Card>
    </Box>
  )
}
